import {EventEmitter, EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {Player, PlayerContractCareerEndNotification, PlayerContractNegotiation} from "@/modules/team/entity";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import PlayerCareerEnder from "@/modules/team/services/PlayerCareerEnder";
import {BeforeSeasonEvaluationEvent} from "@/modules/season/events/BeforeSeasonEvaluationEvent";
import {Season} from "@/modules/season/entity";
import PlayerContractReleaser from "@/modules/team/services/PlayerContractReleaser";
import {BeforePlayerCareerPlanedEndEvent} from "@/modules/team/events/BeforePlayerCareerPlanedEndEvent";
import {NegotiationCloser} from "@/modules/diplomacy/services/NegotiationCloser";
import {Notifier} from "@/modules/notifications/services/Notifier";
import NewcomerYouthPlayerFactory from "@/modules/youth/services/factories/NewcomerYouthPlayerFactory";
import {BeforePlayerCareerEndEvent} from "@/modules/team/events/BeforePlayerCareerEndEvent";
import {NewcomerRecruiter} from "@/modules/youth/services/NewcomerRecruiter";
import chance from "@/modules/base/services/chance";
import {PlayerContractExtender} from "@/modules/team/services/PlayerContractExtender";
import {Router} from "vue-router";
import {TYPES} from "@/modules/vue";
import {Application} from "@/di";
import GlobalResolver from "@/modules/base/services/GlobalResolver";

@injectable()
export class PlayerContractEventSubscriber implements EventSubscriber {
  constructor(
    @inject(Application) private app: Application,
    @inject(GlobalResolver) private globalResolver: GlobalResolver,
    @inject(NegotiationCloser) private negotiationCloser: NegotiationCloser,
    @inject(Notifier) private notifier: Notifier,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(EventEmitter) private eventEmitter: EventEmitter,
    @inject(PlayerCareerEnder) private playerCareerEnder: PlayerCareerEnder,
    @inject(PlayerContractReleaser) private playerContractReleaser: PlayerContractReleaser,
    @inject(NewcomerYouthPlayerFactory) private newcomerYouthPlayerFactory: NewcomerYouthPlayerFactory,
    @inject(NewcomerRecruiter) private newcomerRecruiter: NewcomerRecruiter,
    @inject(PlayerContractExtender) private playerContractExtender: PlayerContractExtender,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterDayEvaluationEvent, callback: this.onAfterDayEvaluationEvent},
      {event: BeforeSeasonEvaluationEvent, callback: this.onBeforeSeasonEvaluationEvent},
      {event: BeforePlayerCareerPlanedEndEvent, callback: this.onBeforePlayerCareerPlanedEndEvent},
      {event: BeforePlayerCareerEndEvent, callback: this.onBeforePlayerCareerEndEvent},
    ];
  }

  async onBeforePlayerCareerEndEvent(event: BeforePlayerCareerEndEvent) {
    if (event.player.team?.user) {
      return;
    }

    const player = await this.newcomerYouthPlayerFactory.make(event.player.team!);
    await player.save();

    await this.newcomerRecruiter.transfer(player, event.player.team!);

    await player.remove();
  }

  async onAfterDayEvaluationEvent(event: AfterDayEvaluationEvent) {
    if (event.season.currentDate.date() !== 1) {
      return;
    }

    const players = await Player
      .createQueryBuilder('player')
      .leftJoinAndSelect('player.team', 'team')
      .innerJoinAndSelect('player.country', 'country')
      .innerJoinAndSelect('country.region', 'region')
      .where('player.age >= :minAge', {minAge: this.configRepository.get('players.contract.endCareer.minAge')})
      .andWhere('player.endsCareer = false AND player.careerEndedAt IS NULL')
      .getMany();

    await players
      .filter((player: Player) => this.configRepository.get('players.contract.endCareer.chance')(player))
      .forEachAsync(async (player: Player) => {
        await this.playerCareerEnder.plan(player);
        console.debug(`Player decided to end career: ${player.name} (${player.age})`);
      });
  }

  async onBeforeSeasonEvaluationEvent(event: BeforeSeasonEvaluationEvent) {
    await this.handleCareerEnds(event.season);
    await this.handleExpiringContracts();
    await this.handleContractDurationDecrease();
  }

  private async handleCareerEnds(season: Season) {
    const players = await Player
      .createQueryBuilder('player')
      .leftJoinAndSelect('player.team', 'team')
      .innerJoinAndSelect('player.country', 'country')
      .innerJoinAndSelect('country.region', 'region')
      .leftJoinAndSelect('team.country', 'teamCountry')
      .leftJoinAndSelect('teamCountry.region', 'teamRegion')
      .where('player.endsCareer = true AND player.contractYears = 1')
      .getMany();

    const userPlayers = players.filter(p => p.team && p.team!.user);

    await this.playerCareerEnder.endMany(season, players);

    if (userPlayers.length > 0) {
      await this.globalResolver.startPromise(() => {
        this.app.get<Router>(TYPES.VueRouter).push({
          name: 'players.career-ended',
          query: {ids: userPlayers.map(p => p.id)}
        });
      });
    }
  }

  private async handleExpiringContracts() {
    await (
      await Player
        .createQueryBuilder('player')
        .innerJoinAndSelect('player.team', 'team')
        .where('player.contractYears = 1 AND player.careerEndedAt IS NULL')
        .getMany()
    )
      .forEachAsync(async (p: Player) => {
        if (
          await this.playerContractExtender.can(p)
          && chance.bool({likelihood: this.configRepository.get('players.contract.chanceOfExtendingContract')(p) * 100})
        ) {
          await this.playerContractExtender.extend(p);
          return;
        }

        await this.playerContractReleaser.release(p);
      });
  }

  private async handleContractDurationDecrease() {
    await Player
      .createQueryBuilder('player')
      .update()
      .set({contractYears: () => 'contractYears - 1'})
      .where('player.careerEndedAt IS NULL')
      .execute();

    console.debug(`Decreased all contract durations by 1.`);
  }

  public async onBeforePlayerCareerPlanedEndEvent(event: BeforePlayerCareerPlanedEndEvent) {
    await (
      await PlayerContractNegotiation
        .createQueryBuilder('n')
        .innerJoinAndSelect('n.target', 'player')
        .where('player.id = :playerId', {playerId: event.player.id})
        .getMany()
    )
      .forEachAsync(async (n: PlayerContractNegotiation) => {
        await this.negotiationCloser.abort(n);

        if (event.player.team && event.player.team!.user) {
          await this.notifier.notify(
            new PlayerContractCareerEndNotification().setPayload(event.player)
          );
        }
      });
  }
}